import { isDefined } from "@clipboard-health/util-ts";
import { post } from "@src/appV2/api/api";
import { API_TYPES } from "@src/appV2/api/constants";
import { environmentConfig } from "@src/appV2/environment";
import { type ResponseWithError } from "@src/appV2/ExperimentalHomeHealth/Cases/types";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib/analytics/events";
import { logEvent } from "@src/appV2/lib/analytics/log";
import { InterviewType } from "@src/appV2/PlacementApplications/types/fetchInterviews.schema";
import { useMutation, type UseMutationOptions } from "@tanstack/react-query";
import { type AxiosError } from "axios";
import { z } from "zod";

const interviewResponseSchema = z.object({
  data: z.object({
    type: z.literal(API_TYPES.interview),
    id: z.string(),
    attributes: z.object({
      start: z.string(),
      end: z.string(),
    }),
  }),
});

type InterviewResponse = z.infer<typeof interviewResponseSchema>;

const createInterviewBodySchema = z.object({
  data: z.object({
    type: z.literal(API_TYPES.interview),
    attributes: z.object({
      start: z.string(),
      end: z.string(),
      type: z.nativeEnum(InterviewType).optional(),
    }),
    relationships: z.object({
      rescheduledInterview: z
        .object({
          data: z.object({
            type: z.literal(API_TYPES.interview),
            id: z.string(),
          }),
        })
        .optional(),
      placementApplication: z
        .object({
          data: z.object({
            type: z.literal(API_TYPES.placementApplication),
            id: z.string(),
          }),
        })
        .optional(),
    }),
  }),
  meta: z.object({
    bypassPlacementDocumentRequirementCheck: z.boolean().optional(),
    bypassInterviewAvailabilityCheck: z.boolean().optional(),
    notifyInPlacementChatChannel: z.boolean().optional(),
  }),
});

// FIXME: Automatically disabled during ESLint config update
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const createInterviewParamsSchema = z.object({
  relatedResource: z.object({
    type: z.enum([API_TYPES.interview, API_TYPES.placementApplication]),
    id: z.string(),
  }),
  start: z.date(),
  end: z.date(),
  interviewType: z.nativeEnum(InterviewType).optional(),
  bypassPlacementDocumentRequirementCheck: z.boolean().optional(),
  bypassInterviewAvailabilityCheck: z.boolean().optional(),
  notifyInPlacementChatChannel: z.boolean().optional(),
});

type CreateInterviewParams = z.infer<typeof createInterviewParamsSchema>;

export function useCreateInterview(
  options: UseMutationOptions<
    InterviewResponse,
    AxiosError<ResponseWithError>,
    CreateInterviewParams
  > = {}
) {
  return useMutation({
    mutationFn: async (params) => {
      const {
        relatedResource,
        start,
        end,
        interviewType,
        bypassPlacementDocumentRequirementCheck,
        bypassInterviewAvailabilityCheck,
        notifyInPlacementChatChannel,
      } = params;

      const response = await post({
        url: `${environmentConfig.REACT_APP_BASE_API_URL}/interviews`,
        data: {
          data: {
            type: API_TYPES.interview,
            attributes: {
              start: start.toISOString(),
              end: end.toISOString(),
              ...(isDefined(interviewType) && { type: interviewType }),
            },
            relationships: {
              ...(relatedResource.type === API_TYPES.interview && {
                rescheduledInterview: {
                  data: {
                    type: API_TYPES.interview,
                    id: relatedResource.id,
                  },
                },
              }),
              ...(relatedResource.type === API_TYPES.placementApplication && {
                placementApplication: {
                  data: {
                    type: API_TYPES.placementApplication,
                    id: relatedResource.id,
                  },
                },
              }),
            },
          },
          meta: {
            bypassPlacementDocumentRequirementCheck,
            bypassInterviewAvailabilityCheck,
            notifyInPlacementChatChannel,
          },
        },
        requestSchema: createInterviewBodySchema,
        responseSchema: interviewResponseSchema,
      });

      logEvent(APP_V2_APP_EVENTS.INTERVIEW_CREATED, {
        interviewId: response.data.data.id,
      });

      return response.data;
    },
    meta: {
      logErrorMessage: APP_V2_APP_EVENTS.INTERVIEW_CREATION_FAILURE,
    },
    ...options,
  });
}
